import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ActionMenu, type ActionMenuItem } from '@/kit';
import { platformApi } from '../../../api/endpoints/platform';
import { handleApiError } from '../../../api/errorMapping';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { PendingInfraModal } from './PendingInfraModal';
import type { PlatformTenantListItemLocal } from '../../../api/types/PlatformLocal';

interface Props {
    tenant: PlatformTenantListItemLocal;
    /** Abre el modal de detalle del padre (estado vive en TenantsTable). */
    onView: (t: PlatformTenantListItemLocal) => void;
    /** Abre el modal de edición del padre (estado vive en TenantsTable). */
    onEdit: (t: PlatformTenantListItemLocal) => void;
}

/**
 * Menú "⋮" de acciones por fila de tenant. Usa el <ActionMenu> del kit (items declarativos con
 * permission/scope). Ver detalle / Editar abren modales del padre vía callbacks; Suspender/Activar se
 * resuelven acá con ConfirmDialog + mutación (cada acción re-enforzada en el backend). Suspender
 * visible en active, Activar en suspended. Cada item gateado por su permiso
 * (platform.tenant.view/update/suspend/activate) scope PLATFORM; el kit filtra con usePermissions()
 * y oculta el "⋮" si no queda ningún item visible (reemplaza el guard manual ACTION_PERMS.some).
 */
export function TenantRowActions({ tenant, onView, onEdit }: Props) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [confirmSuspend, setConfirmSuspend] = useState(false);
    // Modal de faltantes: se abre solo si "Revisar infra" confirma que el tenant SIGUE pendiente
    // (el item de lista no trae infraRequirements → los tomamos de la respuesta de verify-infra).
    const [pendingInfra, setPendingInfra] = useState<string[] | null>(null);

    const invalidate = (): Promise<void> =>
        qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });

    const suspendMut = useMutation({
        mutationFn: () => platformApi.suspendTenant(tenant.tenantId),
        onSuccess: async () => {
            toast.success('Tenant suspendido');
            setConfirmSuspend(false);
            await invalidate();
        },
        onError: handleApiError,
    });

    const activateMut = useMutation({
        mutationFn: () => platformApi.activateTenant(tenant.tenantId),
        onSuccess: async () => {
            toast.success('Tenant activado');
            await invalidate();
        },
        onError: handleApiError,
    });

    // DEC-RBAC-059: re-chequea la infra del silo. Si ya está lista → el backend deja el tenant `active`
    // y cerramos con éxito; si aún faltan tablas → abrimos el modal con la lista actualizada.
    const verifyInfraMut = useMutation({
        mutationFn: () => platformApi.verifyTenantInfra(tenant.tenantId),
        onSuccess: async (res) => {
            await invalidate();
            if (res.status === 'active') {
                toast.success('Infraestructura lista. Tenant activado.');
                setPendingInfra(null);
            } else {
                setPendingInfra(res.infraRequirements ?? []);
            }
        },
        onError: handleApiError,
    });

    const items: ActionMenuItem[] = [
        // Corazón de Fase 2: navega al drill-down del tenant (gestión de sus recursos).
        // Gateado por el mismo permiso que Ver detalle (platform.tenant.view).
        {
            label: 'Administrar',
            permission: 'platform.tenant.view',
            scope: 'PLATFORM',
            onSelect: () => {
                void navigate({
                    to: '/platform/tenants/$tenantId/users',
                    params: { tenantId: tenant.tenantId },
                });
            },
        },
        {
            label: 'Ver detalle',
            permission: 'platform.tenant.view',
            scope: 'PLATFORM',
            onSelect: () => {
                onView(tenant);
            },
        },
        {
            label: 'Editar',
            permission: 'platform.tenant.update',
            scope: 'PLATFORM',
            onSelect: () => {
                onEdit(tenant);
            },
        },
        ...(tenant.status === 'active'
            ? [
                  {
                      label: 'Suspender',
                      permission: 'platform.tenant.suspend',
                      scope: 'PLATFORM',
                      destructive: true,
                      onSelect: () => {
                          setConfirmSuspend(true);
                      },
                  } satisfies ActionMenuItem,
              ]
            : []),
        ...(tenant.status === 'suspended'
            ? [
                  {
                      label: 'Activar',
                      permission: 'platform.tenant.activate',
                      scope: 'PLATFORM',
                      onSelect: () => {
                          activateMut.mutate();
                      },
                  } satisfies ActionMenuItem,
              ]
            : []),
        ...(tenant.status === 'pending-infra'
            ? [
                  {
                      label: 'Revisar infra',
                      permission: 'platform.tenant.update',
                      scope: 'PLATFORM',
                      onSelect: () => {
                          verifyInfraMut.mutate();
                      },
                  } satisfies ActionMenuItem,
              ]
            : []),
    ];

    return (
        <>
            <ActionMenu items={items} />

            <ConfirmDialog
                open={confirmSuspend}
                onOpenChange={setConfirmSuspend}
                title="Suspender tenant"
                description={`${tenant.displayName} quedará suspendido. ¿Continuar?`}
                confirmLabel="Suspender"
                destructive
                onConfirm={() => {
                    suspendMut.mutate();
                }}
            />

            {pendingInfra !== null && (
                <PendingInfraModal
                    open
                    onOpenChange={(open) => {
                        if (!open) setPendingInfra(null);
                    }}
                    tenantId={tenant.tenantId}
                    infraRequirements={pendingInfra}
                />
            )}
        </>
    );
}
